import React from 'react';
import { graphql } from 'gatsby';

//@ts-ignore
import * as styles from './image-with-cta.module.css';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';

import {
    KontentStringValue,
    CTAConfiguration,
    GeneralImages,
    BrandColor,
    KontentRichText,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    imagePreset,
    getDisplayDimensions,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import PlainTextElement from '../Global/Headlines';
import LazyImage from '../Global/LazyImage';

type ShowHide = 'show' | 'hide';

interface Props extends CTAConfiguration, GeneralImages {
    kontentItemId?: string;
    sub_headline?: KontentStringValue;
    show_cta: KontentRadioMultiChoice<ShowHide>;
    headline?: KontentStringValue;
    content_panel__content_panel_color?: BrandColor;
    content_panel__content_panel_font_color?: BrandColor;
    description_copy?: KontentRichText;
    component_anchor_name: KontentStringValue;
}

const ImageWithCta: React.FC<Props> = ({
    sub_headline,
    show_cta,
    headline,
    general_large___small_image_selection__small,
    general_large___small_image_selection__large,
    description_copy,
    cta_configuration__cta_window_action,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_location,
    cta_configuration__cta_label,
    content_panel__content_panel_font_color,
    content_panel__content_panel_color,
    component_anchor_name,
    kontentItemId,
    ...props
}) => {
    const largeImage = getMediaData(
        general_large___small_image_selection__large
    );

    const smallImage = getMediaData(
        general_large___small_image_selection__small
    );
    const anchorTarget =
        getMultiChoice(cta_configuration__cta_window_action) === 'new_window'
            ? '_blank'
            : '_self';
    const contentPanelFontColor = getColor(
        content_panel__content_panel_font_color
    ) as any;
    const contentPanelColor = getColor(
        content_panel__content_panel_color
    ) as any;
    const authorStyles = {
        color: contentPanelFontColor,
        backgroundColor: contentPanelColor,
    };

    const renderCta = () => {
        if (
            !cta_configuration__cta_label?.value ||
            getMultiChoice(show_cta) === 'hide'
        ) {
            return null;
        }

        return (
            <div className={styles.ctaContainer}>
                {cta_configuration__cta_location && (
                    <CtaLink
                        href={BuildLinkHref(cta_configuration__cta_location)}
                        target={anchorTarget}
                    >
                        {cta_configuration__cta_label?.value}
                    </CtaLink>
                )}
            </div>
        );
    };

    return (
        <section
            className={styles.wrapper}
            id={component_anchor_name?.value}
            data-kontent-item-id={kontentItemId}
        >
            {largeImage ? (
                <picture
                    className={styles.picture}
                    data-kontent-element-codename="general_large___small_image_selection__large"
                >
                    {smallImage && (
                        <source
                            media="(max-width:576px)"
                            srcSet={buildKontentSrcSet(
                                smallImage.url,
                                imagePreset.halfWidth.mobile.w,
                                {
                                    fm: 'webp' as const,
                                    q: imagePreset.halfWidth.mobile.q,
                                }
                            )}
                            sizes="100vw"
                            type="image/webp"
                        />
                    )}
                    <LazyImage
                        src={optimizeKontentImageUrl(
                            largeImage.url,
                            imagePreset.fullWidth.desktop
                        )}
                        srcSet={buildKontentSrcSet(
                            largeImage.url,
                            imagePreset.fullWidth.desktop.w,
                            {
                                fm: 'webp' as const,
                                q: imagePreset.fullWidth.desktop.q,
                            }
                        )}
                        sizes="100vw"
                        alt={largeImage.description || ''}
                        {...getDisplayDimensions(
                            largeImage.width,
                            largeImage.height,
                            imagePreset.fullWidth.desktop.w
                        )}
                    />
                </picture>
            ) : null}

            <div
                className={styles.headlineContentContainer}
                style={authorStyles}
            >
                {headline && (
                    <h2 className={styles.headline}>
                        <PlainTextElement
                            text={headline}
                            elementCodename="headline"
                        />
                    </h2>
                )}
                {sub_headline && (
                    <p
                        className={styles.subheadline}
                        data-kontent-element-codename="sub_headline"
                    >
                        {sub_headline.value}
                    </p>
                )}
            </div>
            <div className={styles.descriptionContentContainer}>
                <div className={styles.descriptionContent}>
                    {description_copy?.value && (
                        <div className={styles.descriptionCopy}>
                            {RichText(
                                description_copy,
                                false,
                                undefined,
                                undefined,
                                'description_copy'
                            )}
                        </div>
                    )}
                    {renderCta()}
                </div>
            </div>
        </section>
    );
};

export default ImageWithCta;

export const fragmentImageWithCta = graphql`
    fragment ImageWithCta on kontent_item_component___image_with_cta {
        id
        elements {
            component_anchor_name {
                value
            }
            sub_headline {
                value
            }
            show_cta {
                value {
                    codename
                }
            }
            headline {
                value
            }
            general_large___small_image_selection__small {
                value {
                    ... on kontent_item_media___image {
                        elements {
                            file {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                        }
                    }
                }
            }
            general_large___small_image_selection__large {
                value {
                    ... on kontent_item_media___image {
                        elements {
                            file {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                        }
                    }
                }
            }
            description_copy {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_label {
                value
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            component_anchor_name {
                value
            }
        }
    }
`;
